import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ShoppingBag, X } from 'lucide-react'
import { cms } from '@/lib/cms'
import { formatPrice } from '@/lib/format'
import ProductTile from '@/components/landing/product-tile'
import { useSite } from '@/components/landing/site-provider'

interface BagPanelProps {
    onNavigate?: () => void
}

const BagPanel: FC<BagPanelProps> = ({ onNavigate }): ReactNode => {
    const { bagProducts, removeFromBag, clearBag, user } = useSite()
    const total = bagProducts.reduce((sum, product) => sum + product.price, 0)

    return (
        <>
            <div className='flex items-center justify-between px-3 pb-2 pt-2'>
                <p className='text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-cream/50'>
                    Keranjang
                </p>

                {bagProducts.length > 0 && (
                    <p className='text-xs text-cream/55'>
                        {bagProducts.length} file
                    </p>
                )}
            </div>

            {bagProducts.length === 0 ? (
                <div className='flex flex-col items-center px-6 pb-6 pt-3 text-center'>
                    <span className='grid size-12 place-items-center rounded-2xl bg-cream/5 text-cream/60'>
                        <ShoppingBag aria-hidden className='size-5' />
                    </span>

                    <p className='mt-3 font-heading font-soft text-lg font-medium text-cream'>
                        Keranjang masih kosong
                    </p>

                    <p className='mt-1 text-sm text-cream/60'>
                        {user ? 'Ketuk ikon tas di bawah ebook mana pun untuk menambahkannya.' : 'Masuk dulu untuk mulai belanja, keranjangmu tersimpan di akun.'}
                    </p>

                    {!user && (
                        <Link
                            href='/masuk'
                            onClick={onNavigate}
                            className='mt-4 inline-flex rounded-full bg-cream px-4 py-2.5 text-sm font-semibold text-espresso transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema'
                        >
                            Masuk atau daftar
                        </Link>
                    )}
                </div>
            ) : (
                <>
                    <ul className='flex flex-col gap-0.5'>
                        {bagProducts.map((product) => (
                            <li key={product.id} className='flex items-center gap-3 rounded-2xl p-2'>
                                <ProductTile product={product} className='size-11 shrink-0 rounded-xl' />

                                <div className='min-w-0 flex-1'>
                                    <p {...cms('products', product.id, 'title')} className='truncate font-heading font-soft text-[0.9375rem] font-medium leading-tight text-cream'>
                                        {product.title}
                                    </p>

                                    <p className='mt-0.5 text-xs tabular-nums text-cream/55'>
                                        <span {...cms('products', product.id, 'format')}>{product.format}</span> · {formatPrice(product.price)}
                                    </p>
                                </div>

                                <button
                                    type='button'
                                    onClick={() => removeFromBag(product.id)}
                                    aria-label={`Hapus ${product.title} dari keranjang`}
                                    className='grid size-8 shrink-0 cursor-pointer place-items-center rounded-full text-cream/60 transition-colors hover:bg-cream/10 hover:text-cream focus-visible:outline-2 focus-visible:outline-crema'
                                >
                                    <X aria-hidden className='size-4' />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className='mx-2 mt-2 flex items-center justify-between border-t border-cream/10 px-1 pb-1 pt-3'>
                        <span className='text-sm text-cream/70'>Total</span>

                        <span className='font-heading font-soft text-xl font-medium tabular-nums text-cream'>
                            {formatPrice(total)}
                        </span>
                    </div>

                    <Link
                        href='/checkout'
                        onClick={onNavigate}
                        className='mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-cream px-4 py-3 text-sm font-semibold text-espresso transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema'
                    >
                        Lanjut ke checkout
                        <ArrowRight aria-hidden className='size-4' />
                    </Link>

                    <div className='mt-1 grid grid-cols-2 gap-1'>
                        <Link
                            href='/keranjang'
                            onClick={onNavigate}
                            className='rounded-2xl px-4 py-2.5 text-center text-sm font-medium text-cream/70 transition-colors hover:bg-cream/5 hover:text-cream focus-visible:outline-2 focus-visible:outline-crema'
                        >
                            Lihat keranjang
                        </Link>

                        <button
                            type='button'
                            onClick={clearBag}
                            className='cursor-pointer rounded-2xl px-4 py-2.5 text-sm font-medium text-cream/70 transition-colors hover:bg-cream/5 hover:text-cream focus-visible:outline-2 focus-visible:outline-crema'
                        >
                            Kosongkan
                        </button>
                    </div>
                </>
            )}
        </>
    )
}

export default BagPanel
